import type { ClaimJobResponse } from '@runhuman/shared';

/**
 * Claim a job from the Activity iframe.
 *
 * Hits a parallel `/api/discord/activity/jobs/:jobId/claim` endpoint that runs
 * through `discordActivityAuthGuard` and reuses the existing `claimJobHandler`.
 * The auth guard sets `request.user` from the Discord-OAuth-token mapping, so
 * the existing handler runs unchanged.
 *
 * `claimSource: 'discord-activity'` lets analytics distinguish Tester Lounge
 * claims from web-portal and mobile claims. Discord Activity is treated as a
 * desktop-capable surface — `DESKTOP_CLAIM_SOURCES` (in shared) routes it
 * through the same desktop-only / any gates as `tester-portal`.
 *
 * Returns the full `ClaimJobResponse` (including `webTestLink`) — the caller
 * hands `webTestLink` straight to `openExternalLink` so the URL shape stays
 * server-owned and the iframe never has to know about testerTokens.
 */
const apiPrefix = (() => {
  if (typeof window === 'undefined') return '/api';
  if (window.location.hostname.endsWith('.discordsays.com')) return '/.proxy/api';
  return '/api';
})();

export async function claimJob(jobId: string, accessToken: string): Promise<ClaimJobResponse> {
  const response = await fetch(`${apiPrefix}/discord/activity/jobs/${jobId}/claim`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ claimSource: 'discord-activity' }),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Claim failed: ${response.status} ${body}`);
  }

  return (await response.json()) as ClaimJobResponse;
}
